import { CHARACTER_WHITESPACE, EMPTY_LIST } from '../../../constants/common'
import { SUBMENU_PARAM_ARCHIVE_VALUE } from '../../../constants/route'
import { getPermanentFilters } from '../../../utils/filter'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../../utils/massActions'
import { updatePaginationCount } from '../../../utils/pagination'
import {
  getIsListingActionRestoreMass,
  getListingUnitLabel,
  LISTING_ACTION_RESTORE,
  LISTING_ACTION_RESTORE_MASS,
} from '../../RentalPortalListing'

import {
  RENTAL_PORTAL_ARCHIVE_LISTINGS_ORDERING,
  RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE,
  RENTAL_PORTAL_ARCHIVE_OBJECT_TYPES,
  RENTAL_PORTAL_ARCHIVE_PAGE_SIZE,
  RENTAL_PORTAL_ARCHIVE_RESTORE_DESCRIPTION_KEYS_BY_TYPE,
  RENTAL_PORTAL_ARCHIVE_UNKNOWN_OBJECT_TYPE,
} from './RentalPortalArchive.constants'

export function getIsRentalPortalArchiveMassRestoreAllowed({
  action,
  eligibleCount,
}) {
  return getIsListingActionRestoreMass(action) && eligibleCount !== 0
}

export function getRentalPortalArchiveListingsParams(page) {
  return {
    filters: getPermanentFilters({ sub: SUBMENU_PARAM_ARCHIVE_VALUE }),
    ordering: RENTAL_PORTAL_ARCHIVE_LISTINGS_ORDERING,
    page,
    size: RENTAL_PORTAL_ARCHIVE_PAGE_SIZE,
  }
}

export function toRentalPortalArchiveItem({ listing, t }) {
  return {
    archived_at: listing.updated,
    id: listing.id,
    object_name: getListingUnitLabel({ listing, t }),
    object_type: RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing,
    permissions: { can_restore: listing.permissions.can_restore },
    responsible_user: listing.responsible_manager,
  }
}

export function getRentalPortalArchivePagination(
  pagination,
  responsePagination
) {
  return updatePaginationCount(
    {
      ...pagination,
      pageCount: responsePagination.page_count,
      pageSize: RENTAL_PORTAL_ARCHIVE_PAGE_SIZE,
    },
    responsePagination.total
  )
}

export function getIsRentalPortalArchiveObjectTypeKnown(objectType) {
  return Boolean(
    RENTAL_PORTAL_ARCHIVE_OBJECT_TYPES[objectType] &&
    RENTAL_PORTAL_ARCHIVE_RESTORE_DESCRIPTION_KEYS_BY_TYPE[objectType]
  )
}

export function getRentalPortalArchiveObjectType(objectType) {
  return (
    RENTAL_PORTAL_ARCHIVE_OBJECT_TYPES[objectType] ??
    RENTAL_PORTAL_ARCHIVE_UNKNOWN_OBJECT_TYPE
  )
}

export function getRentalPortalArchiveRestoreDescription({ item, t }) {
  return RENTAL_PORTAL_ARCHIVE_RESTORE_DESCRIPTION_KEYS_BY_TYPE[
    item.object_type
  ]
    .map(key => t(key, { name: item.object_name }))
    .join(CHARACTER_WHITESPACE)
}

export function getIsRentalPortalArchiveRestoreAllowed(item) {
  return Boolean(
    getIsRentalPortalArchiveObjectTypeKnown(item.object_type) &&
    item.permissions?.can_restore
  )
}

export function getRentalPortalArchiveMassActionItems({
  excludeIds,
  items,
  selectAllPages,
  selected,
}) {
  return selectAllPages
    ? items.filter(item => !excludeIds.includes(item.id))
    : selected
}

export function getRentalPortalArchiveMassActionEligibleItems(items) {
  return items.filter(getIsRentalPortalArchiveRestoreAllowed)
}

export function getRentalPortalArchiveMassActionEligibleCount({
  eligibleItems,
  massActionItems,
  selectedSize,
}) {
  return selectedSize - (massActionItems.length - eligibleItems.length)
}

export function getRentalPortalArchiveMassActions({
  eligibleCount,
  selectedSize,
  t,
}) {
  const hasUnusableSelection = selectedSize > 0 && eligibleCount === 0

  const items = [
    {
      disabled: hasUnusableSelection,
      label: t('RentalPortal.ListingRestoreActionOptionKey'),
      permission: true,
      tooltipContent: hasUnusableSelection
        ? t('RentalPortal.ArchiveRestoreMassNotAllowedTooltipKey')
        : null,
      value: LISTING_ACTION_RESTORE_MASS,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getRentalPortalArchiveMassRestoreDescription({
  eligibleCount,
  eligibleItems,
  selectedCount,
  t,
}) {
  const sentences = [
    eligibleCount === 1 && eligibleItems.length === 1
      ? getRentalPortalArchiveRestoreDescription({
          item: eligibleItems[0],
          t,
        })
      : t('RentalPortal.ArchiveRestoreMassConfirmationTextKey', {
          count: eligibleCount,
        }),
    eligibleCount < selectedCount
      ? t('RentalPortal.ArchiveRestoreMassMixedEligibilityKey', {
          eligible: eligibleCount,
          selected: selectedCount,
        })
      : null,
    eligibleCount > 1 &&
    eligibleItems.some(
      item => item.object_type === RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing
    )
      ? t('RentalPortal.ListingRestoreNoRepublishNoticeKey')
      : null,
  ]

  return sentences.filter(Boolean).join(CHARACTER_WHITESPACE)
}

export function getRentalPortalArchiveItemActions(item) {
  const isRestoreAllowed = getIsRentalPortalArchiveRestoreAllowed(item)

  return getIsRentalPortalArchiveObjectTypeKnown(item.object_type)
    ? [
        {
          disabled: !isRestoreAllowed,
          label: 'RentalPortal.ListingRestoreActionOptionKey',
          tooltipContent: isRestoreAllowed
            ? null
            : getRentalPortalArchiveObjectType(item.object_type)
                .restoreNotAllowedTooltip,
          value: LISTING_ACTION_RESTORE,
        },
      ]
    : EMPTY_LIST
}
